import * as React from 'react';
import currency from 'currency.js';
import classnames from 'classnames';
import { connect } from 'react-redux';
import { get } from 'lodash';

import CampaignEntityAvatar from 'components/entity/entity-avatar/CampaignEntityAvatar';
import CampaignEntityName from 'components/entity/entity-name/CampaignEntityName';
import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';
import CollapsibleContainer from 'components/collapsible/container/CollapsibleContainer';

import CampaignCard from 'modules/campaigns/components/campaign-card/CampaignCard';
import CampaignDates from '../campaign-dates/CampaignDates';
import CampaignOverviewGroup from './campaign-overview-group/CampaignOverviewGroup';
import CampaignOverviewProgress from './campaign-overview-progress/CampaignOverviewProgress';
import CampaignOverviewBudgetNote from './budget-note/CampaignOverviewBudgetNote';

import { updateBudgetNote } from 'modules/campaigns/reducers/campaignBrief';

import { __t, applyTranslations } from 'utils/language';

import type {
  BrandProfile,
  Campaign,
  CampaignOverview as CampaignOverviewType,
  CampaignPageType
} from 'types';

applyTranslations(require('./campaign-overview.i18n.json'));

import './campaign-overview.scss';

import type { SyntheticEvent } from 'react';

type StateProps = {
  brandProfile: BrandProfile,
  overview: CampaignOverviewType
};

type DispatchProps = {
  dispatchUpdateBudgetNote: (b: number, a: string) => Promise<void>
};

type OwnProps = {
  campaign: Campaign,
  pageType: CampaignPageType
};

type Props = OwnProps & StateProps & DispatchProps;

type State = {
  isBlockCollapsed: boolean,
  isDescriptionCollapsed: boolean
};

class CampaignOverview extends React.Component<Props, State> {
  state = {
    isBlockCollapsed: this.isOverviewCollapsible(),
    isDescriptionCollapsed: !(this.props.pageType === 'payments' && this.props.campaign?.budgetNote)
  };

  componentDidUpdate(prevProps: Props): void {
    const currentBudgetNote = this.props.campaign.budgetNote;
    const prevBudgetNote = prevProps.campaign.budgetNote;
    const isOnPaymentsPage = this.props.pageType === 'payments';

    const shouldExpandCollapsedDescription = isOnPaymentsPage
      && this.state.isDescriptionCollapsed
      && Boolean(currentBudgetNote)
      && currentBudgetNote !== prevBudgetNote;

    // campaign data might be fetched after CampaignOverview rendered on screen
    if (shouldExpandCollapsedDescription) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({
        isDescriptionCollapsed: false
      });
    }
  }

  toggleCollapsed = (event: SyntheticEvent<HTMLElement>) => {
    this.setState(state => ({
      isDescriptionCollapsed: !state.isDescriptionCollapsed
    }));

    event.preventDefault();
  };

  handleToggleTileExpand = () => {
    this.setState((state) => ({
      isBlockCollapsed: !state.isBlockCollapsed
    }));
  };

  handleBudgetNoteUpdate = (newNote: string) => {
    const { campaign, dispatchUpdateBudgetNote } = this.props;
    dispatchUpdateBudgetNote(campaign.id, newNote);
  };

  isOverviewCollapsible(): boolean {
    return !['participants', 'payments'].includes(this.props.pageType);
  }

  renderEntityAvatar(campaign: Campaign): React.ReactNode {
    const { isBlockCollapsed } = this.state;
    const isCollapsible = this.isOverviewCollapsible();
    const avatarClassNames = classnames('campaign-overview__avatar', {
      'campaign-overview__avatar--full': !isCollapsible,
      'campaign-overview__avatar--expanded': isCollapsible && !isBlockCollapsed,
      'campaign-overview__avatar--collapsed': isCollapsible && isBlockCollapsed
    });

    return (
      <CampaignEntityAvatar
        disabled
        form="square"
        campaign={campaign}
        size="customSize"
        className={avatarClassNames}
        applyCustomElementSize={false}
        customSize={{
          width: 222,
          height: 222
        }}
      />
    );
  }

  renderEntityName(campaign: Campaign): React.ReactNode {
    const { isBlockCollapsed } = this.state;

    const classNames = classnames('campaign-overview__title', {
      'campaign-overview__title--collapsed': isBlockCollapsed
    });

    return (
      <CampaignEntityName
        disabled
        campaign={campaign}
        size="inherit"
        className={classNames}
      />
    );
  }

  renderControls(): React.ReactNode {
    const isCollapsible = this.isOverviewCollapsible();
    if (isCollapsible) {
      return null;
    }

    const { pageType } = this.props;
    const { isDescriptionCollapsed } = this.state;
    const classes = classnames('campaign-overview__icon', {
      'campaign-overview__icon--collapsed': isDescriptionCollapsed
    });

    return (
      <div className="campaign-overview__controls">
        <span className="campaign-overview__control-label">
          {__t(`campaign_overview.expandable_section_${pageType}.campaign_description_label`)}
        </span>
        <span
          onClick={this.toggleCollapsed}
          className="campaign-overview__control"
        >
          {isDescriptionCollapsed
            ? __t(`campaign_overview.expandable_section_${pageType}.show_description_label`)
            : __t(`campaign_overview.expandable_section_${pageType}.hide_description_label`)}
        </span>
        <div className={classes}>
          <SVGDynamicIcon
            name="chevron-up-custom-fg"
            width={14}
            height={7}
            customColor="grey"
            clickable
            onClick={this.toggleCollapsed}
          />
        </div>
      </div>
    );
  }

  renderBudget(): React.ReactNode {
    const { campaign, overview } = this.props;
    const plannedBudget = campaign?.budget ?? 0;
    const usedBudget = overview?.usedBudget ?? 0;
    const variablePayments = overview?.legallyBindingVariablePaymentsSum ?? 0;
    const remainingBudget = currency(plannedBudget).subtract(usedBudget).value;

    return (
      <div className="campaign-overview__budget">
        <CampaignOverviewGroup
          title={__t('campaign_overview.planned_budget')}
          budget={plannedBudget}
          className="campaign-overview__budget-column"
        />
        <div className="campaign-overview__budget-used">
          <div className="campaign-overview__budget-overview">
            <CampaignOverviewGroup
              title={__t('campaign_overview.pending_budget')}
              budget={get(overview, 'pendingOverview.budget', 0)}
            />
            <CampaignOverviewGroup
              title={__t('campaign_overview.agreed_budget')}
              budget={get(overview, 'acceptedOverview.budget', 0)}
            />
            <CampaignOverviewGroup
              title={__t('campaign_overview.variable_payments_budget')}
              budget={variablePayments}
            />
          </div>
          <div className="campaign-overview__sign">=</div>
          <CampaignOverviewGroup
            title={__t('campaign_overview.used_budget')}
            budget={usedBudget}
            className="campaign-overview__budget-column"
          />
        </div>
        <CampaignOverviewGroup
          title={__t('campaign_overview.remaining_budget')}
          budget={remainingBudget}
          className="campaign-overview__budget-column"
        />
      </div>
    );
  }

  renderContent = (campaign: Campaign, overview: CampaignOverviewType) => {
    const { isBlockCollapsed } = this.state;
    const isCollapsible = this.isOverviewCollapsible();

    const expandControlClassNames = classnames('campaign-overview__icon', {
      'campaign-overview__icon--collapsed': isBlockCollapsed
    });

    const contentClassNames = classnames('campaign-overview__content', {
      'campaign-overview__content--collapsed': isBlockCollapsed
    });

    const infoClassNames = classnames('campaign-overview__info-content', {
      'campaign-overview__info-content--collapsed': isBlockCollapsed
    });

    return (
      <div className={contentClassNames}>
        <div className="campaign-overview__header">
          {this.renderEntityName(campaign)}
          <CampaignDates campaign={campaign} />
          {isCollapsible && (
            <div className={expandControlClassNames}>
              <SVGDynamicIcon
                name="chevron-up-custom-fg"
                width={14}
                height={7}
                customColor="grey"
                clickable
                onClick={this.handleToggleTileExpand}
              />
            </div>
          )}
        </div>
        <div className={infoClassNames}>
          <CampaignOverviewProgress
            campaign={campaign}
            overview={overview}
            title={__t('campaign_overview.budget')}
          />
          {this.renderBudget()}
        </div>
        {this.renderControls()}
      </div>
    );
  };

  renderDetails(): React.ReactNode {
    const { pageType } = this.props;

    if (this.isOverviewCollapsible()) {
      return null;
    }

    const { isDescriptionCollapsed } = this.state;

    return (
      <CollapsibleContainer isExpanded={!isDescriptionCollapsed}>
        {pageType === 'participants' && this.renderBackground()}
        {pageType === 'payments' && this.renderBudgetNote()}
      </CollapsibleContainer>
    );
  }

  renderBudgetNote(): React.ReactNode {
    const { campaign } = this.props;
    return (
      <CampaignOverviewBudgetNote
        campaign={campaign}
        onBudgetNoteUpdate={this.handleBudgetNoteUpdate}
      />
    );
  }

  renderBackground(): React.ReactNode {
    const { brandProfile, campaign } = this.props;
    const { isDescriptionCollapsed } = this.state;

    return (
      <React.Fragment>
        <CampaignCard
          layout="horizontal"
          view="compact"
          campaign={campaign}
          brandProfile={brandProfile}
          showExpanded
          isForBrand
        />
        {
          !isDescriptionCollapsed && (
            <div className="campaign-overview__footer">
              <div
                className="campaign-overview__control"
                onClick={this.toggleCollapsed}
              >
                {__t('campaign_overview.expandable_section_participants.hide_description_label')}
              </div>
            </div>
          )
        }
      </React.Fragment>
    );
  }

  render() {
    const { campaign, overview } = this.props;

    if (!campaign || !campaign.name || !campaign.startDate || !campaign.endDate) {
      return null;
    }

    return (
      <div className="campaign-overview">
        <div className="campaign-overview__main">
          {this.renderEntityAvatar(campaign)}
          {this.renderContent(campaign, overview)}
        </div>
        {this.renderDetails()}
      </div>
    );
  }
}


const mapStateToProps = ({ brandProfile, campaignOverview }): StateProps => ({
  brandProfile: brandProfile.profile,
  overview: campaignOverview.overview
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchUpdateBudgetNote: (campaignId: number, budgetNote: string) =>
    dispatch(updateBudgetNote(campaignId, budgetNote))
});

export default connect(mapStateToProps, mapDispatchToProps)(CampaignOverview);
